import { css } from '@emotion/react';

//import slideshowStyles from './components/slideshow/styles';

/*
 * maps {key: value} to {key:'var(--NAME-KEY)'}
 */
function applyObjToTheme(theme, obj, name, prefix = '') {
	theme[name] = {};
	Object.keys(obj).forEach((k) => {
		if (typeof obj[k] === 'object') {
			theme[name][k] = {};
			applyObjToTheme(theme[name], obj[k], k, `${prefix}${name}-`);
		} else {
			theme[name][k] = `var(--${prefix}${name}-${k})`;
		}
	});
}

/*
 * maps {key: value} to '--NAME-KEY: value
 */
function applyObjToCss(obj, name) {
	return Object.keys(obj)
		.map((k) => {
			if (typeof obj[k] === 'object') {
				return applyObjToCss(obj[k], `${name}-${k}`);
			}
			return `--${name}-${k}: ${obj[k]};`;
		})
		.join('');
}

export let theme = {};

theme.pxToEm = (px) => px / 16 + 'em';
theme.pxToUnit = theme.pxToEm;
theme.pxToPercent = (px) => (100 / 16) * px + '%';
theme.pxToFontSize = theme.pxToPercent;

// css vars can not be used in media queries
theme.breakpoint = {
	mobileNav: theme.pxToEm(768),

	xs: theme.pxToEm(10),
	s: theme.pxToEm(640),
	m: theme.pxToEm(768),
	l: theme.pxToEm(1024),
	ml: theme.pxToEm(1200),
	xl: theme.pxToEm(1920),
};

const dimension = {
	container: {
		padding: { s: '1.2em', m: '1.4em', l: '2em' },
		paddingNarrow: { s: '3em', m: '4em', l: '4em' },
		margin: { s: '2.6em', m: '2.6em', l: '3em' },
	},
	spacing: {
		s: theme.pxToEm(40),
		m: theme.pxToEm(50),
		l: theme.pxToEm(60),
	},
	gap: {
		s: theme.pxToEm(12),
		m: theme.pxToEm(16),
		l: theme.pxToEm(20),
	},
	maxWidth: theme.pxToEm(1400),
	maxWidthNarrow: theme.pxToEm(1000),
	element: {
		margin: {
			top: {
				s: theme.pxToEm(20),
				m: theme.pxToEm(40),
				l: theme.pxToEm(60),
			},
			bottom: {
				s: theme.pxToEm(10),
				m: theme.pxToEm(20),
				l: theme.pxToEm(30),
			},
		},
	},
};
applyObjToTheme(theme, dimension, 'dimension');

const zIndexes = [
	'pageBackground',
	'content',
	'pageNav',
	'lightbox',
	'pageBackgroundLightboxButton',
	'header',
	'mobileNav',
	'loadingScreen',
];
const zIndex = {};
zIndexes.forEach((k, i) => {
	zIndex[k] = (i + 1) * 10;
});
applyObjToTheme(theme, zIndex, 'zIndex');

const font = {
	default: {
		family: 'Lato, serif',
		weight: {
			thin: 300,
			regular: 400,
			medium: 700,
			bold: 900,
		},
		size: {
			xs: {
				s: theme.pxToPercent(12),
				m: theme.pxToPercent(12),
				l: theme.pxToPercent(12),
			},
			s: {
				s: theme.pxToPercent(16),
				m: theme.pxToPercent(16),
				l: theme.pxToPercent(18),
			},
			m: {
				s: theme.pxToPercent(18),
				m: theme.pxToPercent(20),
				l: theme.pxToPercent(24),
			},
			l: {
				s: theme.pxToPercent(24),
				m: theme.pxToPercent(38),
				l: theme.pxToPercent(64),
			},
			xl: {
				s: theme.pxToPercent(42),
				m: theme.pxToPercent(76),
				l: theme.pxToPercent(128),
			},
			xxl: {
				s: theme.pxToPercent(60),
				m: theme.pxToPercent(120),
				l: theme.pxToPercent(200),
			},
		},
		lineHeight: {
			xs: {
				s: 1.1,
				m: 1.1,
				l: 1.1,
			},
			s: {
				s: 1.25,
				m: 1.25,
				l: 1.25,
			},
			m: {
				s: 1.25,
				m: 1.25,
				l: 1.25,
			},
			l: {
				s: 1.25,
				m: 1.25,
				l: 1.25,
			},
			xl: {
				s: 1.25,
				m: 1.25,
				l: 1.25,
			},
			xxl: {
				s: 1,
				m: 1,
				l: 1,
			},
		},
	},
};

applyObjToTheme(theme, font, 'font');
theme.typography = {
	getStyle: ({
		theme,
		size = 'm',
		weight = 'thin',
		font = 'default',
		style = 'normal',
		decoration = 'initial',
		lineHeight,
	}) => {
		return css`
			font-family: ${theme.font[font].family};
			font-size: ${theme.font[font].size[size].s};
			font-weight: ${theme.font[font].weight[weight]};
			font-style: ${style};
			line-height: ${lineHeight || theme.font[font].lineHeight[size].s};
			text-decoration: ${decoration};

			@media (min-width: ${theme.breakpoint.m}) {
				font-size: ${theme.font[font].size[size].m};
				line-height: ${lineHeight || theme.font[font].lineHeight[size].m};
			}

			@media (min-width: ${theme.breakpoint.l}) {
				font-size: ${theme.font[font].size[size].l};
				line-height: ${lineHeight || theme.font[font].lineHeight[size].l};
			}
		`;
	},
};

export const colorPalettes = {};
colorPalettes.light = {
	fg: '#fff',
	bg: '#062746',

	fgContrast: 'var(--color-bg)',
	bgContrast: 'var(--color-fg)',

	grey1: '#e3e3e3',
	grey2: '#a8a8a8',
	grey3: '#727272',
	grey4: '#3e3e3e',

	grey1Contrast: 'var(--color-bg)',
	grey2Contrast: 'var(--color-bg)',
	grey3Contrast: 'var(--color-fg)',
	grey4Contrast: 'var(--color-fg)',

	primary: 'rgb(7,72,147)',
	primaryDark: '#062746',
	primaryLight: 'rgb(45 125 203)',

	primaryContrast: 'var(--color-fg)',
	primaryDarkContrast: 'var(--color-fg)',
	primaryLightContrast: 'var(--color-bg)',

	overlayBackground: 'rgba(0, 0, 0, .2)',
	loadingBackground: 'rgba(0, 0, 0, .1)',

	link: 'var(--color-fg)',
	linkActive: 'var(--color-fg)',
	linkVisited: 'var(--color-fg)',

	selection: 'var(--color-secondaryContrast)',
	selectionBackground: 'var(--color-secondary)',

	success: '#3b9e3b',
	successContrast: '#fff',

	warning: '#ed8a15',
	warningContrast: '#000',

	error: '#ef1448',
	errorContrast: '#fff',
};

applyObjToTheme(theme, colorPalettes.light, 'color');

theme.getColors = ({ theme, key, inversed = false }) => css`
	color: ${theme.color[key + (inversed ? 'Contrast' : '')]};
	background: ${theme.color[key + (!inversed ? 'Contrast' : '')]};
`;

theme = {
	...theme,

	resetButton: css`
		cursor: pointer;
		appearance: none;
		font: inherit;
		background: transparent;
		color: inherit;
		border: 0;
		margin: 0;
		padding: 0;
		box-sizing: border-box;
		border-radius: 0;

		&:disabled {
			cursor: default;
		}
	`,

	button: ({ theme, isPrimary = true }) => [
		theme.resetButton,
		css`
			text-decoration: none;
			color: ${isPrimary ? theme.color.primaryContrast : theme.color.fg};
			background-color: ${isPrimary ? theme.color.primary : theme.color.grey2Contrast};
			cursor: pointer;
			padding: 0.2em 0.4em;
			position: relative;
			border: none;
			border-radius: 0;

			&:disabled {
				background-color: ${theme.color.grey1};
				color: ${theme.color.grey1Contrast};
				cursor: inherit;
			}

			&:not(:disabled) {
				&:active,
				&:focus,
				&:hover {
					background-color: ${theme.color.primaryContrast};
					color: ${theme.color.primary};
				}
			}
		`,
	],

	row: ({ theme }) => css`
		margin: calc(${theme.dimension.gap.s} / -2);

		@media (min-width: ${theme.breakpoint.m}) {
			margin: calc(${theme.dimension.gap.m} / -2);
		}

		@media (min-width: ${theme.breakpoint.l}) {
			margin: calc(${theme.dimension.gap.l} / -2);
		}
	`,

	col: ({ theme }) => css`
		margin: calc(${theme.dimension.gap.s} / 2);

		@media (min-width: ${theme.breakpoint.m}) {
			margin: calc(${theme.dimension.gap.m} / 2);
		}

		@media (min-width: ${theme.breakpoint.l}) {
			margin: calc(${theme.dimension.gap.l} / 2);
		}
	`,

	container: ({ theme, limit = true, narrow = false, padding = true, margin = false }) => [
		limit
			? css`
					max-width: ${narrow ? theme.dimension.maxWidthNarrow : theme.dimension.maxWidth};
					margin-left: auto;
					margin-right: auto;
			  `
			: null,
		padding
			? narrow
				? css`
						box-sizing: border-box;
						padding-left: ${theme.dimension.container.paddingNarrow.s};
						padding-right: ${theme.dimension.container.paddingNarrow.s};

						@media (min-width: ${theme.breakpoint.m}) {
							padding-left: ${theme.dimension.container.paddingNarrow.m};
							padding-right: ${theme.dimension.container.paddingNarrow.m};
						}

						@media (min-width: ${theme.breakpoint.l}) {
							padding-left: ${theme.dimension.container.paddingNarrow.l};
							padding-right: ${theme.dimension.container.paddingNarrow.l};
						}
				  `
				: css`
						box-sizing: border-box;
						padding-left: ${theme.dimension.container.padding.s};
						padding-right: ${theme.dimension.container.padding.s};

						@media (min-width: ${theme.breakpoint.m}) {
							padding-left: ${theme.dimension.container.padding.m};
							padding-right: ${theme.dimension.container.padding.m};
						}

						@media (min-width: ${theme.breakpoint.l}) {
							padding-left: ${theme.dimension.container.padding.l};
							padding-right: ${theme.dimension.container.padding.l};
						}
				  `
			: null,
		margin
			? css`
					margin-top: ${theme.dimension.container.margin.s};
					margin-bottom: ${theme.dimension.container.margin.s};

					@media (min-width: ${theme.breakpoint.m}) {
						margin-top: ${theme.dimension.container.margin.m};
						margin-bottom: ${theme.dimension.container.margin.m};
					}

					@media (min-width: ${theme.breakpoint.l}) {
						margin-top: ${theme.dimension.container.margin.l};
						margin-bottom: ${theme.dimension.container.margin.l};
					}
			  `
			: null,
	],

	createPadding: ({ theme, s, m, l }) => css`
		padding: ${s || theme.dimension.container.padding.s};

		@media (min-width: ${theme.breakpoint.m}) {
			padding: ${m || theme.dimension.container.padding.m};
		}

		@media (min-width: ${theme.breakpoint.l}) {
			padding: ${l || theme.dimension.container.padding.l};
		}
	`,

	createMargin: ({ theme, s = '1em', m = '1.4em', l = '2em' }) => css`
		margin: ${s};

		@media (min-width: ${theme.breakpoint.m}) {
			margin: ${m};
		}

		@media (min-width: ${theme.breakpoint.l}) {
			margin: ${l};
		}
	`,

	createGap: ({ theme, s, m, l }) => css`
		gap: ${(s !== undefined && s) || theme.dimension.gap.s};

		@media (min-width: ${theme.breakpoint.m}) {
			gap: ${(m !== undefined && m) || theme.dimension.gap.m};
		}

		@media (min-width: ${theme.breakpoint.l}) {
			gap: ${(l !== undefined && l) || theme.dimension.gap.l};
		}
	`,
};

export const globalStyles = [
	css`
		/* lato-300 - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: normal;
			font-weight: 300;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.eot'); /* IE9 Compat Modes */
			src: local(''), url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300.svg#Lato') format('svg'); /* Legacy iOS */
		}

		/* lato-300italic - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: italic;
			font-weight: 300;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.eot'); /* IE9 Compat Modes */
			src: local(''),
				url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-300italic.svg#Lato')
					format('svg'); /* Legacy iOS */
		}

		/* lato-regular - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: normal;
			font-weight: 400;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.eot'); /* IE9 Compat Modes */
			src: local(''),
				url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-regular.svg#Lato') format('svg'); /* Legacy iOS */
		}

		/* lato-italic - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: italic;
			font-weight: 400;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.eot'); /* IE9 Compat Modes */
			src: local(''),
				url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-italic.svg#Lato') format('svg'); /* Legacy iOS */
		}

		/* lato-700 - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: normal;
			font-weight: 700;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.eot'); /* IE9 Compat Modes */
			src: local(''), url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700.svg#Lato') format('svg'); /* Legacy iOS */
		}

		/* lato-700italic - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: italic;
			font-weight: 700;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.eot'); /* IE9 Compat Modes */
			src: local(''),
				url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-700italic.svg#Lato')
					format('svg'); /* Legacy iOS */
		}

		/* lato-900 - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: normal;
			font-weight: 900;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.eot'); /* IE9 Compat Modes */
			src: local(''), url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900.svg#Lato') format('svg'); /* Legacy iOS */
		}

		/* lato-900italic - latin-ext_latin */
		@font-face {
			font-family: 'Lato';
			font-style: italic;
			font-weight: 900;
			src: url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.eot'); /* IE9 Compat Modes */
			src: local(''),
				url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.eot?#iefix') format('embedded-opentype'),
				/* IE6-IE8 */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.woff2') format('woff2'),
				/* Super Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.woff') format('woff'),
				/* Modern Browsers */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.ttf') format('truetype'),
				/* Safari, Android, iOS */ url('/_resources/app/fonts/lato-v23-latin-ext_latin-900italic.svg#Lato')
					format('svg'); /* Legacy iOS */
		}

		:root {
			${applyObjToCss(colorPalettes.light, 'color')}
			${applyObjToCss(zIndex, 'zIndex')}
			${applyObjToCss(dimension, 'dimension')}
			${applyObjToCss(font, 'font')}
		}

		html,
		body {
			width: 100%;
			height: 100%;
			padding: 0;
			margin: 0;
			font-size: 100%;
			font-family: ${theme.font.default.family};
			font-weight: ${theme.font.default.weight.regular};
			font-style: normal;
			line-height: 1.2;
			color: ${theme.color.fg};
			background-color: ${theme.color.bg};
			overflow: hidden;
		}

		body {
			-moz-osx-font-smoothing: grayscale;
			font-size-adjust: none;
			-webkit-text-size-adjust: none;
			box-sizing: border-box;
			font-size: 100%;
		}

		#root {
			width: 100%;
			height: 100%;
		}

		::selection {
			background: ${theme.color.selectionBackground};
			color: ${theme.color.selection};
			text-shadow: none;
		}

		a {
			color: ${theme.color.link};

			&:active {
				color: ${theme.color.linkActive};
			}

			&:visited {
				color: ${theme.color.linkVisited};
			}
		}

		.ot-floating-button__front {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xml:space='preserve' viewBox='0 0 50 50'%3E%3Cg fill='none' stroke='%23fff'%3E%3Ccircle cx='25' cy='25' r='24.5' fill='%23062746'/%3E%3Cpath stroke-linejoin='round' d='M35.7 18.3c-2.5 0-4.5-2-4.5-4.5v-.3c-1 .8-2.2 1.4-3.6 1.4-3 0-5.4-2.4-5.4-5.4a15.1 15.1 0 1 0 16.2 8 5 5 0 0 1-2.7.8z' /%3E%3Ccircle cx='19.2' cy='19.7' r='3.4' /%3E%3Ccircle cx='20.5' cy='31.1' r='2.9' /%3E%3Ccircle cx='28.8' cy='27.2' r='1.7' /%3E%3Ccircle cx='29.7' cy='22.1' r='1' /%3E%3Ccircle cx='30' cy='33.5' r='.9' /%3E%3Ccircle cx='13.8' cy='25.6' r='1' /%3E%3C/g%3E%3C/svg%3E") !important;
		}
	`,
];

export default theme;
